import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LoadState } from "./enums";
import { createNavigateSmartUrl, getBasePath } from "./util";
import { backgroundRefreshTimeoutMs, backgroundRefreshTimeoutNotVisibleMs } from "./globals";
import { NavigateLockContext, OverlayContext, ScrollContext } from "./App";

// Automatically fetches in a certain interval or if the browser url changes.
// If onlyOnDeepStateChange == true, a deep comparison using JSON.stringify() will be performed between the current state and the last state and only
// if the state changed, a load will be performed.
// If onlyLoadIfInitUrl is true, the URL at the initial load will be saved and future loads will only be performed if the current URL matches the initial URL.
// (useful to avoid loads on page leave)
export function useAutoLoad(onLoadSuccess, onLoadError, onBeforeLoad, addStates=[], onlyOnDeepStateChange=true, noLoadOnUrlChange=false, noLoadOnInit=false,
    onlyLoadIfInitUrl=false, useLoadingOverlay=false)
{
    const location = useLocation();

    const overlay = useContext(OverlayContext);

    const initRef = useRef(true);
    const initUrlRef = useRef("");
    const loadTimeoutRef = useRef();
    const unmountRef = useRef(false);
    const countRef = useRef(0);
    const lastStatesJsonRef = useRef(null);
    const oneLoadFinished = useRef(false); // Becomes true after at least one load has been finished

    // We do not use a loading ref as we want to initiate ALL fetches and do not abort if a fetch operation is currently ongoing.
    // Otherwise we could lose the most up to date fetch query (e.g. user search).
    // const loadingRef = useRef(0);

    const [loadState, setLoadStateInt] = useState(LoadState.NONE);
    const loadStateRef = useRef(loadState);

    const origStack = (new Error()).stack;


    // FUNCTIONS:

    function setLoadState(state) {
        setLoadStateInt(state);
        loadStateRef.current = state;
    }

    function resetLoading() {
        oneLoadFinished.current = false;
    } 

    function scheduleLoad() {
        if(unmountRef.current) {
            return;
        }

        let refreshTimeoutMs = backgroundRefreshTimeoutMs;

        if(document.visibilityState !== "visible") {
            refreshTimeoutMs = backgroundRefreshTimeoutNotVisibleMs;
        }

        loadTimeoutRef.current = setTimeout(() => {
            load(true, false);
        }, refreshTimeoutMs);
    }

    function shouldInitUrlBeChecked() {
        return !noLoadOnUrlChange && onlyLoadIfInitUrl;
    }

    function load(scheduled, init) {
        // We do not want to remove "init" as this might already be in use by some of the clients implementing useBeforeLoad() etc.
        // Here, it was initially also used to determine the need to set the load state but this is now done using "oneLoadFinished".

        clearTimeout(loadTimeoutRef.current);

        if(unmountRef.current) {
            return;
        }

        if(shouldInitUrlBeChecked()) {
            if(location.pathname !== initUrlRef.current) {
                scheduleLoad();
                return;
            }
        }

        const url = onBeforeLoad(scheduled, init);

        if(!url) {
            scheduleLoad();
            return;
        }

        let changedLoading = false;
    
        // We want to keep displaying an error (also if this is a scheduled load)
        const loadStateWasError = loadStateRef.current === LoadState.LOAD_ERROR;

        // We do not want change the loadState to LOADING again if an error is currently beeing displayed
        if(!oneLoadFinished.current && !loadStateWasError) {
            setLoadState(LoadState.LOADING);

            if(useLoadingOverlay) {
                overlay.incrLoadingCounter();
            }

            changedLoading = true;
        }

        countRef.current += 1; // Retrieve a new id
        const fetchId = countRef.current;
        let responseStatus = -1;

        const opts = {};

        if(!scheduled) {
            opts.headers = {"X-User-Interaction": "x"};
        }

        fetch(url, opts).then(response => {
            responseStatus = response.status;

            if(response.ok) {
                return response.json();
            }

            throw new Error("Reponse status: " + response.status);
        }).then(json => {
            if(changedLoading) {
                // Only set this to false if the load was successfully executed (e.g. dashbaord tab instance is created in the background)
                oneLoadFinished.current = true;

                if(useLoadingOverlay) {
                    overlay.decrLoadingCounter();
                }
            }

            if(countRef.current !== fetchId) {
                console.log("Discarding fetch operation as a newer fetch operation was initiated");
                return;
            }

            try {
                onLoadSuccess(json, scheduled, init); // This can throw an error
                setLoadState(LoadState.LOAD_SUCCESS); // Do not set this before calling onLoadSuccess() as we want to give onLoadSuccess() a chance to throw an
                                                      // exception.
            }
            catch(e) {
                // Do not set the load state on scheduled loads and only on init loads
                if(!scheduled && changedLoading) {
                    setLoadState(LoadState.LOAD_ERROR);
                }
                
                console.log(e);
            }

            scheduleLoad();
        }).catch(error => {
            if(changedLoading) {
                if(useLoadingOverlay) {
                    overlay.decrLoadingCounter();
                }
            }
        
            if(countRef.current !== fetchId) {
                console.log("Discarding fetch operation as a newer fetch operation was initiated");
                return;
            }

            // Do not set the load state on scheduled loads and only on init loads
            // Use loadStateWasError as we want to keep displaying an error (also if this is a scheduled load)
            if((!scheduled && changedLoading) || loadStateWasError) {
                setLoadState(LoadState.LOAD_ERROR);
            }

            console.log(error.stack + "\n\tOrig stack: " + origStack);

            onLoadError(scheduled, init, responseStatus);

            scheduleLoad();
        });
    }

    function onVisibilityChange() {
        if(document.visibilityState === "visible") {
            load(true, false);
        }
    }


    // EFFECTS:

    const triggerStates = addStates;

    if(!noLoadOnUrlChange) {
        triggerStates.push(location.pathname);
    }

    useEffect(() => {
        const cleanupFunction = () => {
            unmountRef.current = true;
            document.removeEventListener("visibilitychange", onVisibilityChange);
        };

        unmountRef.current = false;
        document.addEventListener("visibilitychange", onVisibilityChange);

        let init = false;

        const triggerStatesJson = JSON.stringify(triggerStates);

        if(initRef.current) {
            // Initial effect hook execution

            init = true;
            initRef.current = false;

            if(shouldInitUrlBeChecked()) {
                initUrlRef.current = location.pathname;
            }
        }
        else if(onlyOnDeepStateChange)
        {
            // The hook execution is because of a state

            if(lastStatesJsonRef.current !== null) {
                if(triggerStatesJson === lastStatesJsonRef.current) {
                    // Do not load if no state changed
                    // console.log("no");
                    return cleanupFunction;
                }
            }
        }

        lastStatesJsonRef.current = triggerStatesJson;

        if(!init || !noLoadOnInit) {
            load(false, init);
        }

        return cleanupFunction;
    }, triggerStates);

    return [loadState, setLoadState, resetLoading];
}

// This hook also utilizes the visualViewport API as Safari IOS sometimes does not fire regular resize events when
// the keybaord appears / disappears.
export function useResizeListener(callback) {
    useLayoutEffect(() => {
        function cbRegular() {
            callback(window.innerWidth, window.innerHeight, true);
        }

        function cbVisualViewport () {
            callback(window.visualViewport.width, window.visualViewport.height, false);
        }


        cbRegular();
        cbVisualViewport();


        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', cbVisualViewport);
        }

        window.addEventListener("resize", cbRegular);

        return () => {
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', cbVisualViewport);
            }

            window.removeEventListener("resize", cbRegular);
        };
    }, []);
}

export function useNavigateSmart() {
    const navigate = useNavigate();
    const location = useLocation();

    return (to, options) => {
        const toWithLang = createNavigateSmartUrl(location, to);
        navigate(toWithLang, options);
  };  
}

export function useSafeEdit(editable, obj, oldObj, lockNavigation, compareObjects=undefined) {
    const scroll = useContext(ScrollContext);
    const navigateLock = useContext(NavigateLockContext);

    const [shakingEditBtn, setShakingEditBtn] = useState(false);
    const [displayEditTooltip, setDisplayEditTooltip] = useState(false);

    const editBtnRef = useRef();
    const editBoxRef = useRef();

    const displayEditTooltipRef = useRef();
    const editableRef = useRef();


    // FUNCTIONS:

    function scrollToEditBtnTooltip() {
        if(displayEditTooltipRef.current && editBtnRef.current) {
            const rect = editBtnRef.current.getBoundingClientRect();
            scroll.scrollTo(rect.top, rect.bottom);
        }
    }


    // EFFECTS:

    useLayoutEffect(() => {
        editableRef.current = editable;
    }, [editable])

    useLayoutEffect(() => {
        displayEditTooltipRef.current = displayEditTooltip;
        scrollToEditBtnTooltip();
    }, [displayEditTooltip]);

    useEffect(() => {
        function handleClickAnywhere(e) {
            if (editBoxRef.current && editBoxRef.current.contains(e.target) && !editableRef.current) {  
                setShakingEditBtn(true);
                setDisplayEditTooltip(true);
                scrollToEditBtnTooltip();
            }
            else {
                setDisplayEditTooltip(false);
            }
        }

        document.addEventListener("click", handleClickAnywhere);
        return () => document.removeEventListener("click", handleClickAnywhere);
    }, []);

    useLayoutEffect(() => {
        if(!lockNavigation) {
            return;
        }

        if(editable) {
            if(compareObjects) {
                navigateLock.setLockedFunc(compareObjects(obj, oldObj));
            }
            else {
                navigateLock.setLockedFunc(JSON.stringify(obj) !== JSON.stringify(oldObj));
            }
        }
        else {
            navigateLock.setLockedFunc(false);
        }

        return () => navigateLock.setLockedFunc(false);
    }, [editable, obj, oldObj, lockNavigation]);


    // RENDER:

    return {shakingEditBtn, setShakingEditBtn,
        displayEditTooltip, setDisplayEditTooltip,
        editBtnRef, editBoxRef
    };
}


/*
export function useLoadingOverlay(loadState, name) {
    const overlay = useContext(OverlayContext);

    useLayoutEffect(() => {
        overlay.incrLoadingCounter()

        overlay.setLoadingCounter((oldCounter) => {
            if(loadState === LoadState.LOADING) {
                console.log(name + ": incr: " + (oldCounter + 1));
                return oldCounter + 1;
            }
            else if(loadState === LoadState.LOAD_SUCCESS || loadState === LoadState.LOAD_ERROR) {
                console.log(name + ": descr: " + (oldCounter - 1));
                return oldCounter - 1;
            }

            return oldCounter;
        });

        /// console.log(loadState);
    }, [loadState]);
}
*/

/*
export function useLoadOnStateChange(onLoadSuccess, onLoadError, onBeforeLoad, state, loadingRef) {
    const initRef = useRef(true);
    const lastStateRef = useRef(null);

    const [loadState, setLoadState] = useState(LoadState.NONE);


    // FUNCTIONS:

    function load(init=false) {
        console.log("init: " + init);

        // if(loadingRef.current) {
        //     return;
        // }
// 
        // loadingRef.current = true;

        const url = onBeforeLoad();

        if(!url) {
            return;
        }

        if(init) {
            setLoadState(LoadState.LOADING);
        }

        fetch(url).then(response => {
            if(response.ok) {
                return response.json();
            }

            throw new Error("Reponse status: " + response.status);
        }).then(json => {
            setLoadState(LoadState.LOAD_SUCCESS);

            try {
                onLoadSuccess(json); // This can throw an error
            }
            catch(e) {
                setLoadState(LoadState.LOAD_ERROR);
                console.log(e);
            }
        }).catch(error => {
            console.log(error);
            onLoadError();
            setLoadState(LoadState.LOAD_ERROR);
        }).finally(() => {
            // loadingRef.current = false;
        })
    }


    // EFFECTS:

    useEffect(() => {
        if(JSON.stringify(lastStateRef.current) !== JSON.stringify(state)) {
            lastStateRef.current = state;

            let init = false;

            if(initRef.current) {
                init = true;
                initRef.current = false;
            }

            load(init);
        }
        
    }, [state]);

    return loadState;
}
*/
